import { useEffect, useState } from "react";
import {
	Button,
	Input,
	Select,
	Typography,
	Form,
	Spin,
	Table,
	Card,
	Popconfirm,
	Modal,
} from "antd";
import { UserAddOutlined, UserOutlined, DeleteFilled, QuestionCircleOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import {
	deleteUser,
	fetchOrganzationUsers,
	inviteUser,
	updateUserPermission,
} from "../../redux/adminSlice";

const { Title } = Typography;

const OrganizationUsers = () => {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const { organizationsList, currentOrganizationUsers, loading } = useSelector(
		(state) => state.admin
	);

	const [organization, setOrganization] = useState("");
	const [isModalVisible, setIsModalVisible] = useState(false);

	const getCanWritePermission = (user, organizationId) => {
		if (!user.organization_permissions || !user.organization_permissions[organizationId]) {
			return true;
		}
		return user.organization_permissions[organizationId].canWrite;
	};

	const handlePermissionChange = (user, checked) => {
		const payload = {
			userId: user._id,
			email: user.email,
			organization_permissions: {
				...user.organization_permissions,
				[organization]: { canWrite: checked }
			}
		};
		dispatch(updateUserPermission(payload));
	};

	const addUserAccount = (value) => {
		const { email, role } = value;
		if (email && role) {
			const payload = {
				email,
				organization,
				isAdmin: role === "admin",
				organization_permissions: role === "admin" ? {} : {
					[organization]: { canWrite: role === "editor" }
				}
			};
			dispatch(inviteUser(payload));
			form.resetFields();
			setIsModalVisible(false);
		}
	};

	const removeUserAccount = (value) => {
		const { email } = value;
		if (email) {
			dispatch(deleteUser({ email, organization }));
		}
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const onFinishFailed = (errorInfo) => {
		console.log("Failed:", errorInfo);
	};

	useEffect(() => {
		if (organization) {
			dispatch(fetchOrganzationUsers(organization));

		}
	}, [dispatch, organization]);



	return (
		<>
			<Spin spinning={loading} fullscreen />
			<div style={{
				padding: "24px",
				maxWidth: "100%",
				margin: "0 auto",
			}}>
				<div style={{ marginBottom: "24px" }}>
					<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "32px" }}>
						<UserAddOutlined style={{ fontSize: 24, color: "rgb(4 42 87)" }} />
						<Title level={2} style={{ 
							margin: 0, 
							color: "#374151",
							fontWeight: "600"
						}}>
							Organization Users
						</Title>
					</div>
					
					<div style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						width: "40%",
						gap: "8px"
					}}>
						<Select
							showSearch
							placeholder="Select an organization"
							optionFilterProp="children"
							onSelect={(e) => setOrganization(e)}
							filterOption={filterOption}
							options={organizationsList.map((item) => ({
								label: item.organization_name,
								value: item._id,
							}))}
							size="large"
							style={{ 
								flex: 1,
								minWidth: "300px",
								maxWidth: "350px",
								borderRadius: "5px"
							}}
						/>
						
						{organization && (
							<Button
								type="default"
								icon={<UserAddOutlined />}
								style={{
									background: "rgb(4 42 87)",
									color: "#fff",
									fontSize: "12px",
									padding: "8px 16px",
									borderRadius: "5px",
									border: "none",
									fontWeight: "600",
									whiteSpace: "nowrap"
								}}
								onClick={() => {
									form.resetFields();
									setIsModalVisible(true);
								}}
							>
								Add New User
							</Button>
						)}
					</div>
				</div>

				{organization ? (
					<>



						<Card
							bordered={false}
							style={{
								width: "100%",
								maxWidth: "800px",
								minWidth: "320px",
								boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
								borderRadius: "10px",
							}}
						>
							<div style={{ overflowX: "auto" }}>
								<Table
									dataSource={currentOrganizationUsers}
									rowKey="_id"
									pagination={{
										defaultPageSize: 10,
										showSizeChanger: true,
										showQuickJumper: true,
										pageSizeOptions: ['5', '10', '20', '50'],
									}}
									style={{ marginTop: "16px", minWidth: "500px" }}
									scroll={{ x: true }}
								columns={[
									{
										title: "Email",
										dataIndex: "email",
										key: "email",
										render: (text) => (
											<span style={{ 
												fontWeight: "600", 
												color: "#374151",
												fontSize: "14px"
											}}>
												{text}
											</span>
										)
									},
									{
										title: "Role",
										key: "role",
										align: "center",
										render: (_, record) =>
											record.isAdmin ? (
												<span style={{ 
													fontWeight: "600", 
													color: "rgb(4 42 87)",
													backgroundColor: "#f3f4f6",
													padding: "4px 12px",
													borderRadius: "5px",
													fontSize: "12px"
												}}>
													Admin
												</span>
											) : (
												<Select
													size="middle"
													value={getCanWritePermission(record, organization) ? "editor" : "viewer"}
													onChange={(value) =>
														handlePermissionChange(record, value === "editor")
													}
													style={{ 
														minWidth: 120,
														borderRadius: "5px"
													}}
													options={[
														{
															value: "viewer",
															label: <span style={{ color: "#6b7280" }}>Viewer</span>,
														},
														{
															value: "editor",
															label: <span style={{ color: "#374151" }}>Editor</span>,
														},
													]}
												/>
											),
									},
									{
										title: "Actions",
										key: "actions",
										align: "center",
										render: (_, record) => (
											<Popconfirm
												title="Remove user?"
												description={`Are you sure to remove ${record.email}?`}
												placement="left"
												icon={<QuestionCircleOutlined style={{ color: "red" }} />}
												onConfirm={() => removeUserAccount(record)}
												okText="Remove"
												okType="danger"
												cancelText="Cancel"
											>
												<Button 
													danger 
													size="small" 
													icon={<DeleteFilled />}
													style={{
														borderRadius: "5px",
														fontSize: "12px"
													}}
												>
													Remove
												</Button>
											</Popconfirm>
										),
									},
								]}
								/>
							</div>
						</Card>
					</>
				) : (
					<Card
						bordered={false}
						style={{
							width: "100%",
							maxWidth: "800px",
							minWidth: "320px",
							boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
							borderRadius: "10px",
							textAlign: "center",
							padding: "40px"
						}}
					>
						<UserOutlined style={{
							fontSize: "48px",
							color: "#d1d5db",
							marginBottom: "16px"
						}} />
						<Title level={4} style={{ color: "#6b7280", margin: "0 0 8px" }}>
							Select an Organization
						</Title>
						<p style={{ color: "#9ca3af", margin: 0 }}>
							Choose an organization from the dropdown above to manage users
						</p>
					</Card>
				)}

				<Modal
					title="Add New User"
					open={isModalVisible}
					onCancel={() => setIsModalVisible(false)}
					footer={[
						<Button
							key="cancel"
							onClick={() => setIsModalVisible(false)}
							style={{
								fontSize: "12px",
								padding: "4px 12px",
								borderRadius: "5px",
								color: "#6b7280",
							}}
						>
							Cancel
						</Button>,
						<Button
							key="add"
							type="primary"
							onClick={() => form.submit()}
							style={{
								backgroundColor: "rgb(4 42 87)",
								fontSize: "12px",
								padding: "4px 12px",
								borderRadius: "5px",
								border: "none",
							}}
						>
							Add User
						</Button>,
					]}
					width={500}
				>
					<Form
						layout="vertical"
						onFinish={addUserAccount}
						onFinishFailed={onFinishFailed}
						form={form}
						autoComplete="new-state"
					>
						<Form.Item
							label={
								<span style={{ 
									fontWeight: "600", 
									color: "#374151",
									fontSize: "14px"
								}}>
									Email Address
								</span>
							}
							name="email"
							rules={[
								{
									required: true,
									type: "email",
									message: "Please enter a valid email address!",
								},
							]}
							style={{ marginBottom: "16px" }}
						>
							<Input 
								size="middle" 
								placeholder="Enter user email"
								style={{ 
									borderRadius: "5px",
									border: "1px solid #d1d5db"
								}} 
							/>
						</Form.Item>

						<Form.Item
							label={
								<span style={{ 
									fontWeight: "600", 
									color: "#374151",
									fontSize: "14px"
								}}>
									User Role
								</span>
							}
							name="role"
							initialValue="editor"
							rules={[
								{
									required: true,
									message: "Please select a role!",
								},
							]}
							style={{ marginBottom: "16px" }}
						>
							<Select
								size="middle"
								placeholder="Select user role"
								style={{ 
									borderRadius: "5px",
									width: "100%"
								}}
								options={[
									{
										value: "admin",
										label: <span style={{ color: "#374151", fontWeight: 600 }}>Admin</span>,
									},
									{
										value: "editor",
										label: <span style={{ color: "#374151" }}>Editor</span>,
									},
									{
										value: "viewer",
										label: <span style={{ color: "#6b7280" }}>Viewer</span>,
									},
								]}
							/>
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</>
	);
};

export default OrganizationUsers;
